import { animated, config, useSpring } from '@react-spring/web';
import type { CSSProperties } from 'react';
import styled from 'styled-components';

// svg circles start at 3 o'clock position and go clockwise
const Circle = styled(animated.circle)`
  fill: none;
  stroke: var(--circular-background-stroke, var(--c-nd-700-opaque));
  transform-origin: center;
  transform: rotate(-90deg);
`;

const BackgroundCircle = styled(Circle)`
  opacity: 0.4;
`;

const ForegroundCircle = styled(Circle)`
  stroke: var(--circular-progress-stroke, var(--c-nd-900-opaque));
`;

type Props = {
  progress: number;
  maxProgress: number;
  strokeWidth?: number;
  className?: string;
  style?: CSSProperties;
};

export const CircularProgress = ({
  progress,
  maxProgress,
  strokeWidth = 50,
  className,
  style,
}: Props) => {
  const radius = 80;
  const effectiveRadius = radius - strokeWidth / 2;
  const perimeter = 2 * Math.PI * effectiveRadius;
  const progressLength = perimeter * (progress / maxProgress);
  const remainingLength = perimeter - progressLength;

  const { strokeDasharray } = useSpring({
    config: config.gentle,
    from: { strokeDasharray: `0 ${perimeter}` },
    to: { strokeDasharray: `${progressLength} ${remainingLength}` },
  });

  return (
    <svg
      viewBox="0 0 160 160"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      style={style}
    >
      <BackgroundCircle
        cx={radius}
        cy={radius}
        r={effectiveRadius}
        strokeWidth={strokeWidth}
      />
      <ForegroundCircle
        cx={radius}
        cy={radius}
        r={effectiveRadius}
        strokeWidth={strokeWidth}
        strokeDasharray={strokeDasharray}
      />
    </svg>
  );
};
